import { For, ParentProps, Signal, batch, createEffect, createMemo, createSignal, on, onMount } from 'solid-js'
import { createProxySignal } from 'common'
import styles from './st-time-picker.module.css'
import Dropdown from '../st-dropdown/st-dropdown';
import Select from '../st-select/st-select';
import Input from '../st-input/st-input';

export class TimePickerProps {
    value!: Signal<Date | undefined>
}

interface InputSegment {
    value: string;
    start: number;
    end: number;
}

type SplitTimeOutput = { valid: true; hour: InputSegment; minute: InputSegment; amPm: InputSegment; } | { valid: false; hour: {}; minute: {}; amPm: {}; }

const splitTimeInput = (input: string): SplitTimeOutput => {
    const colonIdx = input.indexOf(':');

    const hourIdx = 0;
    const minuteIdx = colonIdx + 1;
    const amIdx = input.indexOf('AM');
    const pmIdx = input.indexOf('PM');
    const amPmIdx = amIdx !== -1 ? amIdx : pmIdx;

    if (colonIdx === -1 || amPmIdx === -1) return { valid: false, hour: {}, minute: {}, amPm: {} };

    const hour = input.slice(hourIdx, colonIdx).trim();
    const minute = input.slice(minuteIdx, amPmIdx).trim();
    const amPm = input.slice(amPmIdx, amPmIdx + 2).trim();

    return {
        valid: true,
        hour: { value: hour, start: hourIdx, end: hourIdx + hour.length},
        minute: { value: minute, start: minuteIdx, end: minuteIdx + minute.length },
        amPm: { value: amPm, start: amPmIdx, end: amPmIdx + amPm.length }
    };
}

export const parseTimeInput = (input: string): Date | undefined => {
    if (!input) return undefined;

    const { valid, hour, minute, amPm } = splitTimeInput(input);
    if (!valid) return undefined;

    const intHour = parseInt(hour.value!);
    const intMinute = parseInt(minute.value!);

    if (isNaN(intHour) || isNaN(intMinute)) return undefined;

    const dt = new Date(1900, 0, 1, 0, 0, 0, 0);

    if (amPm.value === 'AM') {
        dt.setHours(intHour % 12);
    } else {
        dt.setHours(intHour % 12 + 12);
    }

    dt.setMinutes(intMinute);
    return dt;
}

export default function TimePicker(props: ParentProps<TimePickerProps>) {
    let inputRef!: HTMLInputElement;
    const [value, setValue] = props.value ?? createSignal<Date>();
    const [open, setOpen] = createSignal(false);
    const [inputStr, setInputStr] = createSignal('');
    const [pickerHour, setPickerHour] = createSignal<number>();
    const [pickerMinute, setPickerMinute] = createSignal<number>();
    const [pickerAmPm, setPickerAmPm] = createSignal<'AM' | 'PM' | ''>('');

    const [pickerHourStr, setPickerHourStr] = createProxySignal(() => pickerHour()?.toString() ?? '', (v) => {
        if (v === undefined) return;
        const intV = parseInt(v);
        if (isNaN(intV)) return;

        if (intV < 1) {
            setPickerHour(12)
        } else if (intV > 12) {
            setPickerHour(1)
        } else {
            setPickerHour(intV)
        }
    });

    const [pickerMinuteStr, setPickerMinuteStr] = createProxySignal(
        () => pickerMinute()?.toString().padStart(2, '0') ?? '',
        (v) => {
            if (v === undefined) return;
            const intV = parseInt(v);
            if (isNaN(intV)) return;

            if (intV < 0) {
                setPickerMinute(59)
            } else if (intV > 59) {
                setPickerMinute(0)
            } else {
                setPickerMinute(intV)
            }
        }
    );

    createEffect(on(() => [value()], ([prev]) => {
        const newDt = value();
        if (!newDt) return;

        const amPm = newDt.getHours() < 12 ? 'AM' : 'PM';
        const hour = newDt.getHours() % 12 || 12;
        const minute = newDt.getMinutes();

        batch(() => {
            setPickerAmPm(amPm)
            setPickerHour(hour)
            setPickerMinute(minute)
            setInputStr(`${hour}:${minute.toString().padStart(2, '0')} ${amPm}`)
        })
    }))

    createEffect(on(() => [pickerHour(), pickerMinute(), pickerAmPm()], () => {
        const hour = pickerHour();
        const minute = pickerMinute();
        const amPm = pickerAmPm();

        if (hour === undefined || minute === undefined || amPm === '') return;

        const dt = new Date(1900, 0, 1, 0, 0, 0, 0);

        if (amPm === 'AM') {
            dt.setHours(hour % 12);
        } else {
            dt.setHours(hour % 12 + 12);
        }

        dt.setMinutes(minute);

        if (dt.toISOString() === value()?.toISOString()) return;

        setValue(dt);
    }))
    
    createEffect(() => {
        if (open() && !value()) {
            setValue(new Date())
        }
    })

    const onFocusOut = () => {
        const dt = parseTimeInput(inputStr());
        if (dt) {
            setValue(dt);
        }
    }

    const onKeyDown = (ev: KeyboardEvent) => {


        const originalInput = inputRef.value;
        const { valid, hour, minute, amPm } = splitTimeInput(originalInput);
        const cursorStart = inputRef.selectionStart;
        const isInRange = (cursor: number, segment: InputSegment) => cursor >= segment.start && cursor <= segment.end;
        const reselect = (key: Exclude<keyof SplitTimeOutput, 'valid'>) => {
            const newInput = inputRef.value;
            const newSplit = splitTimeInput(newInput);
            if (!newSplit.valid) return;

            const newSegment = newSplit[key];
            setTimeout(() => inputRef.setSelectionRange(newSegment.start!, newSegment.end!), 0)
        }

        if (!valid) return;
        if (cursorStart === null) return;

        switch (ev.key) {
            case 'ArrowUp':
            case 'ArrowDown':
                {
                    ev.preventDefault();
                    
                    const direction = ev.key === 'ArrowUp' ? 1 : -1;
            
                    if (isInRange(cursorStart, hour)) {
                        setPickerHour((pickerHour() ?? 0) + direction)
                        reselect('hour')
                    } else if (isInRange(cursorStart, minute)) {
                        const newMinute = (pickerMinute() ?? 0) + direction;
                        if (newMinute < 0) {
                            setPickerMinute(59)
                        } else if (newMinute > 59) {
                            setPickerMinute(0)
                        } else {
                            setPickerMinute(newMinute)
                        }
                        reselect('minute')
                    } else if (isInRange(cursorStart, amPm)) {
                        const values = ['AM', 'PM'] as const;
                        const idx = values.findIndex(v => v === pickerAmPm());
                        const newIdx = (idx + direction + values.length) % values.length;
                        setPickerAmPm(values[newIdx])
                        reselect('amPm')
                    }
                }
                break;
            case 'Tab':
                {
                    const direction = ev.shiftKey ? -1 : 1;
                    const locations = [hour, minute, amPm];
                    const current = locations.findIndex(l => isInRange(cursorStart, l));
                    const next = current + direction;

                    if (next >= 0 && next < locations.length) {
                        ev.preventDefault(); 
                        inputRef.setSelectionRange(locations[next].start!, locations[next].end!)
                    }
                }
                break;
        }
    }

    // if (import.meta.env.DEV) {
    //     onMount(() => {
    //         setOpen(true)
    //     })
    // }



    return (
        <div class={styles['st-time-picker']}>
            <Dropdown open={[open, setOpen]}>
                <Dropdown.Trigger>
                    <Input ref={inputRef} value={[inputStr, setInputStr]} icon={'fa fa-clock'} onFocusOut={onFocusOut} onKeyDown={onKeyDown} />
                </Dropdown.Trigger>
                <Dropdown.Content class={styles['picker']}>

                    <Input value={[pickerHourStr, setPickerHourStr]} type='number' />
                    <Input value={[pickerMinuteStr, setPickerMinuteStr]} type='number' />
                    <Select value={[pickerAmPm, setPickerAmPm]}>
                        <Select.Option value='AM'>AM</Select.Option>
                        <Select.Option value='PM'>PM</Select.Option>
                    </Select>

                    <div style={{"grid-column": "1 / 4"}}>
                        <Clock hour={[pickerHour, setPickerHour]} minute={[pickerMinute, setPickerMinute]} />
                    </div>

                </Dropdown.Content>
            </Dropdown>
        </div>
    );
}

function Clock(props: { hour: Signal<number | undefined>, minute: Signal<number | undefined> }) {
    let clockRef!: HTMLDivElement;

    const [hour, setHour] = props.hour;
    const [minute, setMinute] = props.minute;

    const [hourAngle, setHourAngle] = createSignal(-90);

    createEffect(on(() => [hour()], ([currHour], prev) => {
        if (currHour === undefined) return;

        // Set the initial angle
        if (prev === undefined || prev[0] === undefined) {
            setHourAngle(currHour * 30 - 90);
            return;
        }

        // Calculate the delta angle that results in the shortest path. Rotations are not bounded to 360 degrees.
        const currentAngle = hourAngle();
        const normalizedCurrent = currentAngle % 360;
        const targetAngle = currHour * 30 - 90;

        let delta = targetAngle - normalizedCurrent;

        if (delta > 180) {
            delta -= 360;
        } else if (delta < -180) {
            delta += 360;
        }

        setHourAngle(currentAngle + delta);
    }))

    const [minuteAngle, setMinuteAngle] = createSignal(-90);
    createEffect(on(() => [minute()], ([currMinute], prev) => {
        if (currMinute === undefined) return;

        // Set the initial angle
        if (prev === undefined || prev[0] === undefined) {
            setMinuteAngle(currMinute * 6 - 90);
            return;
        }

        // Calculate the delta angle that results in the shortest path. Rotations are not bounded to 360 degrees.
        const currentAngle = minuteAngle();
        const normalizedCurrent = currentAngle % 360;
        const targetAngle = currMinute * 6 - 90;

        let delta = targetAngle - normalizedCurrent;

        if (delta > 180) {
            delta -= 360;
        } else if (delta < -180) {
            delta += 360;
        }

        setMinuteAngle(currentAngle + delta);
        
    }))

    const [isDraggingMinute, setIsDraggingMinute] = createSignal(false);
    const [isDraggingHour, setIsDraggingHour] = createSignal(false);

    const onMinuteDragStart = (ev: MouseEvent) => { ev.preventDefault(); setIsDraggingMinute(true); }
    const onMinuteDragStop = () => setIsDraggingMinute(false);
    const onMinuteDrag = (ev: MouseEvent) => {
        if (!isDraggingMinute()) return;

        const rect = clockRef.getBoundingClientRect();
        const x = ev.clientX - rect.left - rect.width / 2;
        const y = ev.clientY - rect.top - rect.height / 2;

        const angle = Math.atan2(y, x) * 180 / Math.PI + 90;
        let minute = Math.round(angle / 6) % 60;
        if (minute < 0) {
            minute += 60;
        }
        minute = Math.abs(minute);
        setMinute(minute);
    }

    const onHourDragStart = (ev: MouseEvent) => { ev.preventDefault(); setIsDraggingHour(true); }
    const onHourDragStop = () => setIsDraggingHour(false);
    const onHourDrag = (ev: MouseEvent) => {
        if (!isDraggingHour()) return;

        const rect = clockRef.getBoundingClientRect();
        const x = ev.clientX - rect.left - rect.width / 2;
        const y = ev.clientY - rect.top - rect.height / 2;

        const angle = Math.atan2(y, x) * 180 / Math.PI + 90;
        let hour = Math.round(angle / 30) % 12;
        if (hour < 0) {
            hour += 12;
        }
        hour = Math.abs(hour);
        setHour(hour || 12);
    }

    createEffect(() => {
        if (isDraggingMinute()) {
            document.addEventListener('mousemove', onMinuteDrag)
            document.addEventListener('mouseup', onMinuteDragStop)
        } else {
            document.removeEventListener('mousemove', onMinuteDrag)
            document.removeEventListener('mouseup', onMinuteDragStop)
        }

        if (isDraggingHour()) {
            document.addEventListener('mousemove', onHourDrag)
            document.addEventListener('mouseup', onHourDragStop)
        } else {
            document.removeEventListener('mousemove', onHourDrag)
            document.removeEventListener('mouseup', onHourDragStop)
        }
    })

    return (
        <div class={styles['clock']} ref={clockRef}>
            <div class={`${styles['clock-hour']} ${styles['hand']}`} style={{ rotate: `${hourAngle()}deg`, }} onMouseDown={onHourDragStart} />
            <div class={`${styles['clock-minute']} ${styles['hand']}`} style={{ rotate: `${minuteAngle()}deg`}} onMouseDown={onMinuteDragStart} />

            <For each={[...Array(12).keys()]}>{i => (
                <span class={styles['clock-hour-marker']} style={`--angle: ${i * 30 - 60}deg;`}>{i + 1}</span>
            )}</For>
        </div>
    )
}

TimePicker.defaultProps = new TimePickerProps();